let isCheckingDiscount = false;
/**
 * PurchaseOptionsElement - Custom HTML Element for handling purchase options
 * Handles PDP purchase options UI logic with product toggle support
 */
class PurchaseOptionsElement extends HTMLElement {
  constructor() {
    super();

    // State management
    this.isActive = false;
    this.mutationObserver = null;

    // Cached DOM elements
    this.elements = {};

    // Bind methods to preserve 'this' context
    this.handleTabChange = this.handleTabChange.bind(this);
    this.handleSubscriptionChange = this.handleSubscriptionChange.bind(this);
    this.handleOnetimeChange = this.handleOnetimeChange.bind(this);
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.handleExternalProductChange = this.handleExternalProductChange.bind(this);
    this.handleActiveStateChange = this.handleActiveStateChange.bind(this);

    // Flavor tracking state for analytics payloads
    this.analyticsState = {
      clickCount: 0,
      lastVariantId: '',
      lastFlavor: '',
      lastVariantTitle: ''
    };
  }

  /**
   * Called when element is added to DOM
   */
  connectedCallback() {
    // Expose methods on the element for external access
    this.forceActivate = (defaultToSubscription = true) => {
      this.classList.add('active');
      this.resetToDefaultState(defaultToSubscription);
    };

    this.forceUpdate = () => {
      this.detectActiveState();
      if (this.isActive) {
        this.updatePurchaseType();
      }
    };

    // Use setTimeout to ensure the element is fully rendered in the DOM
    setTimeout(() => {
      this.initializeComponent();
    }, 50);

    // Use setTimeout to ensure the element is fully rendered in the DOM
    setTimeout(() => {
      let productOptionsToggleElements = this.querySelectorAll('.purchase-options-toggle__tabs .purchase-option-tab-c');

      let optionsWrapper = this.querySelector('.purchase-options-toggle');

      if(!optionsWrapper) {
        optionsWrapper = document.querySelector('.purchase-options-toggle');
      }

      if(productOptionsToggleElements.length == 0) {
        productOptionsToggleElements = document.querySelectorAll('.purchase-options-toggle__tabs .purchase-option-tab-c');
      }

      if (optionsWrapper) {
        if (productOptionsToggleElements.length === 3) {
          optionsWrapper.classList.add('is--grid-three-column');
        }
        if (productOptionsToggleElements.length === 4) {
          optionsWrapper.classList.add('is--grid-two-column');
        }
      }
    }, 100);

  }

  /**
   * Called when element is removed from DOM
   */
  disconnectedCallback() {
    this.removeEventListeners();
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  /**
   * Initialize the component and cache DOM elements
   */
  initializeComponent() {
    this.cacheElements();
    this.detectActiveState();
    this.attachEventListeners();
    this.initializeOptions();
    this.syncFlavorTrackingState();
    this.setupMutationObserver();
  }

  /**
   * Cache frequently used DOM elements
   */
  cacheElements() {
    // Tab elements (within this element) - support both buttons and inputs
    this.elements.tabInputs = this.querySelectorAll('.purchase-options__tab-input');
    this.elements.tabButtons = this.querySelectorAll('.purchase-options__tab-nav');
    this.elements.subscriptionOptions = this.querySelectorAll('.subscription-option__input');
    this.elements.onetimeOptions = this.querySelectorAll('.onetime-option__input');

    // Find the closest form - could be ancestor or sibling
    const form = this.closest('form') || this.parentElement?.closest('form') || this.parentElement?.querySelector('form');

    // If we have a form context, query within it; otherwise fallback to scoped queries
    const queryContext = form || this.parentElement || document;

    // Form elements (scope to form/container to support multiple instances)
    this.elements.sellingPlanInput = queryContext.querySelector('input[name="selling_plan"]');
    this.elements.quantityInput = queryContext.querySelector('input[name="quantity"]');
    this.elements.monthsInput = queryContext.querySelector('input[name="properties[_months]"]') ||
      queryContext.querySelector('input[name="properties[_otp]"]');
    this.elements.variantInput = queryContext.querySelector('input[name="id"]');

    // Button elements (scope to container to support multiple instances)
    this.elements.submitButton = queryContext.querySelector('.purchase-summary__add-to-cart');
    this.elements.buttonSpinner = queryContext.querySelector('.loading-overlay__spinner');
    this.elements.buttonText = queryContext.querySelector('.purchase-summary__button-text');

    // Price elements (scope to container to support multiple instances)
    this.elements.totalPriceElement = queryContext.querySelector('.purchase-summary__total-price');
    this.elements.originalPriceElement = queryContext.querySelector('.purchase-summary__original-price');
    this.elements.originalAmountElement = queryContext.querySelector('.purchase-summary__original-amount');
    this.elements.savingsElement = queryContext.querySelector('.purchase-summary__savings');
    this.elements.savingsAmountElement = queryContext.querySelector('.purchase-summary__savings-amount');
    this.elements.buttonPrice = queryContext.querySelector('.button-price');
    this.elements.buttonComparePrice = queryContext.querySelector('.button-compare-price');

    // Notification elements (search document-wide as they're global/shared)
    this.elements.cartNotificationWrapper = document.querySelector('.cart-notification-wrapper');
    this.elements.cartNotificationProductName = document.querySelector('.cart-notification__product-name');
    this.elements.errorNotificationWrapper = document.querySelector('.error-notification-wrapper');
    this.elements.errorNotificationText = document.querySelector('.error-notification__text');

    // Log caching info for debugging
    console.log('Cached elements for:', this.id, {
      hasForm: !!form,
      queryContext: form ? 'form' : (this.parentElement ? 'parent' : 'document'),
      hasVariantInput: !!this.elements.variantInput,
      hasSubmitButton: !!this.elements.submitButton
    });
  }

  /**
   * Detect if this purchase option element is currently active
   */
  detectActiveState() {
    try {
      // Check if this element has the 'active' class or is the only purchase option
      // Also check if the parent container is active (for product toggle scenarios)
      const parentContainer = this.closest('[id^="purchase-option-"]');
      const hasActiveClass = this.classList.contains('active');
      const parentIsActive = parentContainer ? parentContainer.classList.contains('active') : false;
      const isOnlyOption = document.querySelectorAll('purchase-options').length === 1;

      // For product toggle scenarios, also check if this is the first/default element when no active state is set
      let isDefaultActive = false;
      if (!hasActiveClass && !parentIsActive && !isOnlyOption) {
        // If this is a product toggle scenario and no element is explicitly active,
        // consider the first purchase-options element as active
        const allPurchaseOptions = document.querySelectorAll('purchase-options');
        if (allPurchaseOptions.length > 0 && allPurchaseOptions[0] === this) {
          // Check if any parent container is marked as active
          const anyParentActive = Array.from(allPurchaseOptions).some(option => {
            const container = option.closest('[id^="purchase-option-"]');
            return container && container.classList.contains('active');
          });

          // If no parent is active, this first element should be considered active
          if (!anyParentActive) {
            isDefaultActive = true;
          }
        }
      }

      const previousState = this.isActive;
      this.isActive = hasActiveClass || parentIsActive || isOnlyOption || isDefaultActive;

      // Log state changes
      if (previousState !== this.isActive) {
        console.log('Active state CHANGED for element:', this.id, 'from', previousState, 'to', this.isActive);
      }

      console.log('Active state detection:', {
        element: this.id,
        hasActiveClass,
        parentIsActive,
        isOnlyOption,
        isDefaultActive,
        finalState: this.isActive,
        stateChanged: previousState !== this.isActive
      });

      return this.isActive;

    } catch (error) {
      console.error('Error in detectActiveState for element:', this.id, error);
      // Fallback: assume not active if there's an error
      this.isActive = false;
      return false;
    }
  }

  /**
   * Attach all event listeners
   */
  attachEventListeners() {
    // Tab switching listeners - support both inputs and buttons
    this.elements.tabInputs.forEach(input => {
      input.addEventListener('change', this.handleTabChange);
    });

    // Add button click listeners
    this.elements.tabButtons.forEach(button => {
      button.addEventListener('click', this.handleTabChange);
    });

    // Form submission listener (only if we have a form within this element)
    const form = this.closest('form')
    if (form) {
      console.log('Adding form submit listener to element:', this.id);
      form.addEventListener('submit', this.handleFormSubmit);
    }

    // Variant change listeners
    window.addEventListener('variant:change', this.handleVariantChange);
    document.addEventListener('variant:change', this.handleVariantChange);

    // External product change listeners
    document.addEventListener('product-tab:change', this.handleExternalProductChange);
    document.addEventListener('product-tab:switched', this.handleExternalProductChange);
    document.addEventListener('purchase-option:activate', this.handleExternalProductChange);

    // Use event delegation for dynamic option changes
    this.addEventListener('change', this.handleOptionChange.bind(this));
  }

  /**
   * Remove event listeners (cleanup)
   */
  removeEventListeners() {
    window.removeEventListener('variant:change', this.handleVariantChange);
    document.removeEventListener('variant:change', this.handleVariantChange);
    document.removeEventListener('product-tab:change', this.handleExternalProductChange);
    document.removeEventListener('product-tab:switched', this.handleExternalProductChange);
    document.removeEventListener('purchase-option:activate', this.handleExternalProductChange);
  }

  /**
   * Handle option changes using event delegation
   */
  handleOptionChange(e) {
    const shouldTrackFlavorChange = Boolean(e?.isTrusted && this.isActive);
    const selectedInput = shouldTrackFlavorChange ? e.target : null;

    if (e.target.matches('.subscription-option__input')) {
      this.handleSubscriptionChange(e);
    } else if (e.target.matches('.onetime-option__input')) {
      this.handleOnetimeChange(e);
    }

    if (shouldTrackFlavorChange && selectedInput) {
      this.trackFlavorChange(selectedInput);
    }
  }

  /**
   * Handle tab switching between subscription and one-time
   */
  handleTabChange(e) {
    console.log('Tab change event triggered for:', this.id);

    // If this is a button click, handle button activation
    if (e && e.target && e.target.classList.contains('purchase-options__tab-nav')) {
      // Clear all button active states
      this.elements.tabButtons.forEach(btn => {
        btn.classList.remove('active');
        btn.setAttribute('aria-selected', 'false');
      });

      // Activate clicked button
      e.target.classList.add('active');
      e.target.setAttribute('aria-selected', 'true');
    }

    this.handleTabSwitch();

    // Only update shared inputs if this element is active
    if (this.isActive) {
      this.updatePurchaseType();
    }
  }

  /**
   * Handle subscription option selection
   */
  handleSubscriptionChange(e) {
    const input = e.target;

    // Remove selected class from all subscription options in this element
    this.querySelectorAll('.subscription-option').forEach(option => {
      option.classList.remove('subscription-option--selected');
    });

    // Also deselect any OTP/onetime options (from OTP block or inline options)
    this.querySelectorAll('.onetime-option').forEach(option => {
      option.classList.remove('onetime-option--selected');
      const otpInput = option.querySelector('.onetime-option__input');
      if (otpInput) otpInput.checked = false;
    });

    // Add selected class to current option
    input.closest('.subscription-option').classList.add('subscription-option--selected');

    // Update hidden inputs and pricing only if this purchase option is active
    if (this.isActive) {
      this.updatePurchaseType();
    }
  }

  /**
   * Handle one-time option selection
   */
  handleOnetimeChange(e) {
    const input = e.target;

    // Remove selected class from all onetime options in this element
    this.querySelectorAll('.onetime-option').forEach(option => {
      option.classList.remove('onetime-option--selected');
    });

    // Also deselect any subscription options
    this.querySelectorAll('.subscription-option').forEach(option => {
      option.classList.remove('subscription-option--selected');
      const subInput = option.querySelector('.subscription-option__input');
      if (subInput) subInput.checked = false;
    });

    // Add selected class to current option
    input.closest('.onetime-option').classList.add('onetime-option--selected');

    // Update hidden inputs and pricing only if this purchase option is active
    if (this.isActive) {
      this.updatePurchaseType();
    }
  }

  /**
   * Handle form submission to cart
   */
  handleFormSubmit(e) {
    // Only process if this element is active
    if (!this.isActive) {
      console.log('Ignoring submit from inactive element:', this.id);
      return;
    }

    e.preventDefault();

    // Check if we should proceed to checkout after adding to cart
    const proceedToCheckout = this.elements.submitButton?.dataset.proceedToCheckout === 'true';

    // Show loading state
    this.setLoadingState(true);
    this.hideNotifications();
    const form = this.closest('form')

    // Get form data
    const formData = new FormData(form);

    // Submit to cart
    let preAddCartProductIds = [];
    window.__pdpUpsellPopupPending = true;

    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        preAddCartProductIds = (cart.items || []).map(item => Number(item.product_id || 0));

        return fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });
      })
      .then(response => response.json())
      .then(data => {
        if (data.status && data.status === 422) {
          throw new Error(data.description || 'Unable to add item to cart');
        }

        console.log('Product added to cart:', data);

        const quickAddOpened = document.querySelector('.drawer.product-quick-add[open="true"]');
        if (quickAddOpened) {
          const closeButton = quickAddOpened.querySelector('.product-quick-add__close.drawer__close');
          closeButton && closeButton.click();
        }

        if (proceedToCheckout) {
          window.__pdpUpsellPopupPending = false;
          setTimeout(() => {
            window.location.href = '/checkout';
          }, 300);
          return;
        }

        // Notify PDP-scoped listeners (upsell popup, analytics, etc.)
        const pdpAddedDetail = this.dispatchPdpAddedEvent(data, preAddCartProductIds);
        window.__pdpUpsellPopupPending = false;

        if (!pdpAddedDetail?.deferCartDrawer) {
          this.showSuccessNotification(data);
        }

        // Update cart count if available
        if (window.theme && window.theme.updateCartCount) {
          window.theme.updateCartCount();
        }

        // Trigger cart update events
        if (!pdpAddedDetail?.deferCartDrawer) {
          if (typeof window.refreshCartDrawer === 'function') {
            window.refreshCartDrawer({ open: true });
          } else {
            document.documentElement.dispatchEvent(
              new CustomEvent('cart:refresh', {
                bubbles: true
              })
            );
          }
        }
      })
      .catch(error => {
        window.__pdpUpsellPopupPending = false;
        console.error('Error adding to cart:', error);
        this.showErrorNotification(
          error.message || 'Failed to add item to cart. Please try again.'
        );
      })
      .finally(() => {
        // Only reset loading state if not redirecting to checkout
        const proceedToCheckout = this.elements.submitButton?.dataset.proceedToCheckout === 'true';
        if (!proceedToCheckout) {
          this.setLoadingState(false);
        }
      });
  }

  /**
   * Broadcast a normalized "PDP added" event so the upsell popup (and any
   * other PDP-scoped listeners) can react to adds coming from this element.
   * Handles both single-item and `{ items: [...] }` cart/add.js responses.
   */
  dispatchPdpAddedEvent(response, preAddCartProductIds = []) {
    if (!response) return null;

    const item = Array.isArray(response.items) ? response.items[0] : response;
    if (!item || !item.product_id) return null;

    const sellingPlan = item.selling_plan_allocation?.selling_plan?.id;

    const eventDetail = {
      productId: Number(item.product_id),
      variantId: Number(item.variant_id || item.id) || null,
      quantity: Number(item.quantity) || 1,
      sellingPlanId: sellingPlan ? String(sellingPlan) : null,
      lineItemKey: item.key || null,
      preAddCartProductIds,
      item,
    };

    document.dispatchEvent(new CustomEvent('arrae:pdp:added', { detail: eventDetail }));
    return eventDetail;
  }

  /**
   * Handle tab switching logic - supports both buttons and inputs
   */
  handleTabSwitch() {
    let selectedTab = null;
    let selectedType = null;

    // First try to find an active button
    const activeButton = this.querySelector('.purchase-options__tab-nav.active');
    if (activeButton) {
      selectedType = activeButton.dataset.purchaseType;
    }

    // Fall back to checked input
    if (!selectedType) {
      selectedTab = this.querySelector('.purchase-options__tab-input:checked');
      if (selectedTab) {
        selectedType = selectedTab.value;
      }
    }

    // If no selection found, default to subscription if available
    if (!selectedType) {
      const subscriptionButton = this.querySelector('.purchase-options__tab-nav[data-purchase-type="subscription"]');
      const subscriptionInput = this.querySelector('input[data-purchase-type="subscription"]');
      const onetimeButton = this.querySelector('.purchase-options__tab-nav[data-purchase-type="onetime"]');
      const onetimeInput = this.querySelector('input[data-purchase-type="onetime"]');

      if (subscriptionButton || subscriptionInput) {
        selectedType = 'subscription';
        if (subscriptionButton) {
          subscriptionButton.classList.add('active');
          subscriptionButton.setAttribute('aria-selected', 'true');
        }
        if (subscriptionInput) {
          subscriptionInput.checked = true;
        }
      } else if (onetimeButton || onetimeInput) {
        selectedType = 'onetime';
        if (onetimeButton) {
          onetimeButton.classList.add('active');
          onetimeButton.setAttribute('aria-selected', 'true');
        }
        if (onetimeInput) {
          onetimeInput.checked = true;
        }
      }
    }

    if (!selectedType) return;

    const allContent = this.querySelectorAll('.purchase-options__content');

    // Hide all content sections
    allContent.forEach(content => {
      content.classList.remove('active');
    });

    // Show the selected content section and select first option
    if (selectedType === 'subscription') {
      const subscriptionContent = this.querySelector('.purchase-options__content--subscription');
      if (subscriptionContent) subscriptionContent.classList.add('active');

      // Select the default/first subscription option
      this.selectDefaultOrFirstOption('.subscription-option__input', '.subscription-option');
    } else {
      const onetimeContent = this.querySelector('.purchase-options__content--onetime');
      if (onetimeContent) onetimeContent.classList.add('active');

      // Select the default/first onetime option
      this.selectDefaultOrFirstOption('.onetime-option__input', '.onetime-option');
    }
  }

  /**
   * Explicitly select subscription tab and trigger all necessary updates
   */
  selectSubscription() {
    try {
      console.log('=== Starting selectSubscription for element:', this.id, 'isActive:', this.isActive);

      // Re-detect active state to ensure we have latest state
      this.detectActiveState();
      console.log('Active state after detection:', this.isActive);

      // Look for both button and input subscription tabs
      const subscriptionButton = this.querySelector('.purchase-options__tab-nav[data-purchase-type="subscription"]');
      const subscriptionInput = this.querySelector('input[data-purchase-type="subscription"]');
      const onetimeButton = this.querySelector('.purchase-options__tab-nav[data-purchase-type="onetime"]');
      const onetimeInput = this.querySelector('input[data-purchase-type="onetime"]');

      if (!subscriptionButton && !subscriptionInput) {
        console.error('No subscription tab found for element:', this.id);
        return;
      }

      console.log('Found subscription tab, proceeding with selection');

      // Step 1: Reset all tabs - handle both buttons and inputs

      // Reset button states
      this.elements.tabButtons.forEach(btn => {
        btn.classList.remove('active');
        btn.setAttribute('aria-selected', 'false');
      });

      // Reset input states
      this.elements.tabInputs.forEach(input => {
        input.checked = false;
      });

      // Activate subscription tab
      if (subscriptionButton) {
        subscriptionButton.classList.add('active');
        subscriptionButton.setAttribute('aria-selected', 'true');
      }
      if (subscriptionInput) {
        subscriptionInput.checked = true;
      }

      // Step 2: Show/hide content sections smoothly
      const subscriptionContent = this.querySelector('.purchase-options__content--subscription');
      const onetimeContent = this.querySelector('.purchase-options__content--onetime');

      // Add smooth transition styles if not already present
      if (subscriptionContent && !subscriptionContent.style.transition) {
        subscriptionContent.style.transition = 'opacity 0.1s ease-in-out';
      }
      if (onetimeContent && !onetimeContent.style.transition) {
        onetimeContent.style.transition = 'opacity 0.1s ease-in-out';
      }

      if (subscriptionContent) {
        subscriptionContent.classList.add('active');
        console.log('Showed subscription content');
      }
      if (onetimeContent) {
        onetimeContent.classList.remove('active');
        console.log('Hid onetime content');
      }

      // Step 3: Select subscription option and trigger events immediately
      try {
        const selectedOption = this.selectDefaultOrFirstOption('.subscription-option__input', '.subscription-option');
        if (selectedOption) {
          console.log('Selected subscription option:', selectedOption.dataset.quantity || selectedOption.value);

          // Trigger events immediately to prevent visual delays
          try {
            // Trigger change event on the subscription option
            const optionChangeEvent = new Event('change', { bubbles: true });
            selectedOption.dispatchEvent(optionChangeEvent);
            console.log('Dispatched change event on subscription option');

            // Trigger appropriate tab change event
            if (subscriptionButton) {
              // For buttons, trigger click event
              const buttonClickEvent = new Event('click', { bubbles: true });
              subscriptionButton.dispatchEvent(buttonClickEvent);
              console.log('Dispatched click event on subscription button');
            }
            if (subscriptionInput) {
              // For inputs, trigger change event
              const inputChangeEvent = new Event('change', { bubbles: true });
              subscriptionInput.dispatchEvent(inputChangeEvent);
              console.log('Dispatched change event on subscription input');
            }

          } catch (eventError) {
            console.error('Error dispatching events:', eventError);
          }

        } else {
          console.warn('No subscription option was selected');
        }
      } catch (optionError) {
        console.error('Error selecting subscription option:', optionError);
      }

      // Step 4: Immediate purchase type update
      try {
        console.log('Immediate purchase type update');
        this.updatePurchaseType();
        console.log('Purchase type update completed');
      } catch (updateError) {
        console.error('Error updating purchase type:', updateError);
        // Retry once after a very short delay
        setTimeout(() => {
          try {
            this.updatePurchaseType();
          } catch (retryError) {
            console.error('Retry failed:', retryError);
          }
        }, 10);
      }

      console.log('=== selectSubscription completed for element:', this.id);

    } catch (error) {
      console.error('Error in selectSubscription for element:', this.id, error);
    }
  }

  /**
   * Select the first option (top option in the list)
   */
  selectFirstOption(optionSelector, containerClass) {
    const options = this.querySelectorAll(optionSelector);

    if (options.length === 0) return null;

    // Uncheck all options and remove selected class
    options.forEach(option => {
      option.checked = false;
      const container = option.closest(containerClass);
      if (container) {
        container.classList.remove(containerClass.replace('.', '') + '--selected');
      }
    });

    // Select the first option
    const firstOption = options[0];
    firstOption.checked = true;
    const container = firstOption.closest(containerClass);
    if (container) {
      container.classList.add(containerClass.replace('.', '') + '--selected');
    }

    return firstOption;
  }

  /**
   * Select the default option (checked) or the first option
   */
  selectDefaultOrFirstOption(optionSelector, containerClass) {
    const options = this.querySelectorAll(optionSelector);
    if (options.length === 0) return null;

    let selectedOption = null;

    // Check if any option is already checked (from HTML rendering)
    options.forEach(option => {
      if (option.checked) {
        selectedOption = option;
      }
    });

    // If no option is checked, default to the first one
    if (!selectedOption) {
      selectedOption = options[0];
      selectedOption.checked = true;
    }

    // Ensure visual states are updated
    options.forEach(option => {
      const container = option.closest(containerClass);
      if (container) {
        if (option === selectedOption) {
          container.classList.add(containerClass.replace('.', '') + '--selected');
        } else {
          container.classList.remove(containerClass.replace('.', '') + '--selected');
          option.checked = false;
        }
      }
    });

    // Trigger change event if needed
    if (selectedOption) {
      const changeEvent = new Event('change', { bubbles: true });
      selectedOption.dispatchEvent(changeEvent);
      console.log('Selected default/first option:', selectedOption.dataset.quantity || selectedOption.value);
    }

    return selectedOption;
  }

  clearOptionSelection(optionSelector, containerClass) {
    this.querySelectorAll(optionSelector).forEach(option => {
      option.checked = false;
    });

    this.querySelectorAll(containerClass).forEach(option => {
      option.classList.remove(`${containerClass.replace('.', '')}--selected`);
    });
  }


  /**
   * Update purchase type and pricing
   */
  parseDatasetJSON(value) {
    if (!value) return null;

    try {
      return JSON.parse(value);
    } catch (error) {
      console.warn('Failed to parse dataset JSON payload', error);
      return null;
    }
  }

  updatePurchaseType() {
    let selectedType = null;

    // First try to find active button
    const activeButton = this.querySelector('.purchase-options__tab-nav.active');
    if (activeButton) {
      selectedType = activeButton.dataset.purchaseType;
    }

    // Fall back to checked input
    if (!selectedType) {
      const selectedTab = this.querySelector('.purchase-options__tab-input:checked');
      if (selectedTab) {
        selectedType = selectedTab.value;
      }
    }

    // Auto-detect type if no tabs found (e.g. single option group provided)
    if (!selectedType) {
      if (this.querySelector('.subscription-option__input:checked')) {
        selectedType = 'subscription';
      } else if (this.querySelector('.onetime-option__input:checked')) {
        selectedType = 'onetime';
      }
    }

    if (!selectedType) {
      console.log('No selected tab found, cannot update purchase type for element:', this.id);
      return;
    }

    console.log('Updating purchase type:', selectedType, 'for element:', this.id);

    if (selectedType === 'subscription') {
      this.updateSubscriptionPurchase();
    } else {
      this.updateOnetimePurchase();
    }

    // Resolve current option for event details
    let currentOption = null;
    if (selectedType === 'subscription') {
      currentOption = this.querySelector('.subscription-option__input:checked');
    } else {
      currentOption = this.querySelector('.onetime-option__input:checked');
    }

    // Trigger custom event for other scripts
    window.dispatchEvent(
      new CustomEvent('purchase-option:change', {
        detail: {
          type: selectedType,
          variantId: this.elements.variantInput ? this.elements.variantInput.value : '',
          sellingPlan: this.elements.sellingPlanInput ? this.elements.sellingPlanInput.value : '',
          quantity: this.elements.quantityInput ? this.elements.quantityInput.value : '1',
          months: this.elements.monthsInput ? this.elements.monthsInput.value : '',
          variantImage: currentOption?.dataset.variantImage || '',
          variantTitle: currentOption?.dataset.variantTitle || '',
          galleryMedia: this.parseDatasetJSON(currentOption?.dataset.galleryMedia)
        }
      })
    );

    this.syncFlavorTrackingState();

    // Update URL
    if (this.isActive) {
      this.updateUrl(
        this.elements.variantInput ? this.elements.variantInput.value : '',
        this.elements.sellingPlanInput ? this.elements.sellingPlanInput.value : '',
        this.elements.quantityInput ? this.elements.quantityInput.value : '1'
      );
    }
  }

  getSelectedFlavorOption() {
    return this.querySelector('.subscription-option__input:checked, .onetime-option__input:checked');
  }

  formatFlavorName(optionInput) {
    if (!optionInput) return '';

    const explicitFlavor = optionInput.dataset.flavor || optionInput.dataset.variantFlavor || '';
    if (explicitFlavor) return explicitFlavor.trim();

    const variantTitle = (optionInput.dataset.variantTitle || '').trim();
    if (!variantTitle) return '';

    const splitPattern = /\s(?:\/|-)\s/;
    if (splitPattern.test(variantTitle)) {
      return variantTitle.split(splitPattern)[0].trim();
    }

    return variantTitle;
  }

  getProductContextForAnalytics() {
    let productId = '';
    let productTitle = '';

    try {
      const productJsonEl = document.querySelector('[data-product-json]');
      if (productJsonEl?.textContent) {
        const productData = JSON.parse(productJsonEl.textContent);
        productId = productData?.id != null ? String(productData.id) : '';
        productTitle = typeof productData?.title === 'string' ? productData.title : '';
      }
    } catch (error) {
      console.warn('Failed to parse product JSON for flavor analytics', error);
    }

    return { productId, productTitle };
  }

  syncFlavorTrackingState() {
    const selectedOption = this.getSelectedFlavorOption();
    if (!selectedOption) return;

    this.analyticsState.lastVariantId = selectedOption.dataset.variantId || selectedOption.value || '';
    this.analyticsState.lastVariantTitle = selectedOption.dataset.variantTitle || '';
    this.analyticsState.lastFlavor = this.formatFlavorName(selectedOption);

    if (this.isActive) {
      window.__pdpFlavorAnalyticsState = window.__pdpFlavorAnalyticsState || { clickCount: 0 };
      window.__pdpFlavorAnalyticsState.lastVariantId = this.analyticsState.lastVariantId;
      window.__pdpFlavorAnalyticsState.lastVariantTitle = this.analyticsState.lastVariantTitle;
      window.__pdpFlavorAnalyticsState.lastFlavor = this.analyticsState.lastFlavor;
    }
  }

  trackFlavorChange(selectedOption) {
    if (!selectedOption) return;

    window.__pdpFlavorAnalyticsState = window.__pdpFlavorAnalyticsState || { clickCount: 0 };
    const globalState = window.__pdpFlavorAnalyticsState;

    const toVariantId = selectedOption.dataset.variantId || selectedOption.value || '';
    const toVariantTitle = selectedOption.dataset.variantTitle || '';
    const toFlavor = this.formatFlavorName(selectedOption);
    const fromVariantId = globalState.lastVariantId || this.analyticsState.lastVariantId || '';
    const fromVariantTitle = globalState.lastVariantTitle || this.analyticsState.lastVariantTitle || '';
    const fromFlavor = globalState.lastFlavor || this.analyticsState.lastFlavor || '';

    if (!toVariantId || toVariantId === fromVariantId) return;

    globalState.clickCount += 1;
    this.analyticsState.clickCount = globalState.clickCount;
    const { productId, productTitle } = this.getProductContextForAnalytics();

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'flavor_change',
      event_category: 'PDP',
      event_label: `${fromFlavor || fromVariantTitle || 'unknown'} -> ${toFlavor || toVariantTitle || 'unknown'}`,
      click_count: globalState.clickCount,
      from_flavor: fromFlavor,
      to_flavor: toFlavor,
      from_variant_id: fromVariantId,
      to_variant_id: toVariantId,
      from_variant_title: fromVariantTitle,
      to_variant_title: toVariantTitle,
      purchase_type: selectedOption.classList.contains('subscription-option__input') ? 'subscription' : 'onetime',
      product_id: productId,
      product_title: productTitle
    });

    globalState.lastVariantId = toVariantId;
    globalState.lastVariantTitle = toVariantTitle;
    globalState.lastFlavor = toFlavor;
  }

  /**
   * Update for subscription purchase
   */
  updateSubscriptionPurchase() {
    const selectedOption = this.querySelector('.subscription-option__input:checked');

    if (selectedOption) {
      console.log('Updating subscription purchase for element:', this.id, 'isActive:', this.isActive);

      // Only update main form inputs if this element is active
      if (this.isActive && this.elements.variantInput && this.elements.sellingPlanInput && this.elements.quantityInput) {
        // For subscription: use variant ID and selling plan, quantity = 1
        this.elements.variantInput.value = selectedOption.dataset.variantId || selectedOption.value;
        this.elements.sellingPlanInput.value = selectedOption.dataset.sellingPlan || '';
        this.elements.quantityInput.value = '1'; // Always 1 for subscription variants

        // Set the months custom property for subscriptions
        if (this.elements.monthsInput) {
          this.elements.monthsInput.setAttribute('name', 'properties[_months]');
          this.elements.monthsInput.value = selectedOption.dataset.months || selectedOption.dataset.quantity || '';
        }

        console.log('Updated main form inputs:', {
          variantId: this.elements.variantInput.value,
          sellingPlan: this.elements.sellingPlanInput.value,
          quantity: this.elements.quantityInput.value,
          months: this.elements.monthsInput ? this.elements.monthsInput.value : 'N/A'
        });

        if (window.enableSavingLogic && window.savingDiscountCode != '') {
          document.body.classList.add('discount-saving-loading');
          runDiscountCheck(window.savingDiscountCode.trim());
        }
      }

      // Always update pricing display for active elements
      if (this.isActive) {
        const planPrice = selectedOption.getAttribute('data-plan-price');
        const planComparePrice = selectedOption.getAttribute('data-plan-compare-price');
        const variantId = selectedOption.dataset.variantId;
        const subscriptionDiscountPercentage = selectedOption.dataset.discountPercentage;
        this.updatePricingDisplay(planPrice, planComparePrice, variantId, subscriptionDiscountPercentage);
      }

      window.bicConfig.variant_id = selectedOption.dataset.variantId;
      if(window.NotifyMe) {
        window.NotifyMe.isGloballyEnabled();
      }
    }
  }

  /**
   * Update for one-time purchase
   */
  updateOnetimePurchase() {
    const selectedOption = this.querySelector('.onetime-option__input:checked');

    if (selectedOption) {
      console.log('Updating one-time purchase for element:', this.id, 'isActive:', this.isActive);

      // Only update main form inputs if this element is active
      if (this.isActive && this.elements.variantInput && this.elements.sellingPlanInput && this.elements.quantityInput) {
        // For one-time: use variant ID, no selling plan, quantity from option
        this.elements.variantInput.value = selectedOption.dataset.variantId || selectedOption.value;
        this.elements.sellingPlanInput.value = ''; // No selling plan for one-time
        this.elements.quantityInput.value = selectedOption.dataset.quantity || 1;

        // For one-time: use OTP property and set to selected quantity
        if (this.elements.monthsInput) {
          this.elements.monthsInput.setAttribute('name', 'properties[_otp]');
          this.elements.monthsInput.value = selectedOption.dataset.quantity || '';
        }

        console.log('Updated main form inputs:', {
          variantId: this.elements.variantInput.value,
          sellingPlan: this.elements.sellingPlanInput.value,
          quantity: this.elements.quantityInput.value,
          otp: this.elements.monthsInput ? this.elements.monthsInput.value : 'N/A'
        });

        const oneTimePurchaseOptions = document.querySelectorAll('.purchase-options__content.purchase-options__content--onetime.is--new-layout .onetime-group');
        const oneTimePurchaseWrapper = document.querySelector('.purchase-options__content.purchase-options__content--onetime.is--new-layout');

        if (oneTimePurchaseOptions.length >= 3 && oneTimePurchaseOptions.length <= 4) {
          if(oneTimePurchaseWrapper) {
            oneTimePurchaseWrapper.classList.add(`grid-${oneTimePurchaseOptions.length}-layout`, 'is--grid');
          }
        }

        this.updateDynamicDescription(this.elements.variantInput.value);

        if (window.enableSavingLogic && window.savingDiscountCode != '') {
          document.body.classList.add('discount-saving-loading');
          runDiscountCheck(window.savingDiscountCode.trim());
        }
      }

      // Always update pricing display for active elements
      if (this.isActive) {
        const totalPrice = selectedOption.getAttribute('data-total-price');
        const comparePrice = selectedOption.getAttribute('data-compare-price');
        const variantId = selectedOption.dataset.variantId;
        const quantity = selectedOption.dataset.quantity || 1;
        const otpDiscount = selectedOption.dataset.otpDiscount || 0;
        this.updatePricingDisplay(totalPrice, comparePrice, variantId, null, quantity, true, otpDiscount);
      }

      window.bicConfig.variant_id = selectedOption.dataset.variantId;
      if(window.NotifyMe) {
        window.NotifyMe.isGloballyEnabled();
      }

      const oneTimeItems = document.querySelectorAll('.onetime-options__list .onetime-group');
      oneTimeItems.forEach(o_item => {
        const input = o_item.querySelector('input');
        const isCrossedItem = window.disabledVariantIds.includes(input.getAttribute('data-variant-id'));

        isCrossedItem && o_item.classList.add('is--crossed-line');
      });

      const isCrossedlLine = selectedOption.closest('.is--crossed-line');
      const addToCartButton = document.querySelector('.purchase-summary .purchase-summary__add-to-cart');

      if(isCrossedlLine && addToCartButton) {
        setTimeout(() => {
          addToCartButton.setAttribute('disabled', 'true');
          addToCartButton.classList.add('is--out-of-stock');
        }, 0);
      } else if(!isCrossedlLine && addToCartButton) {
        setTimeout(() => {
          addToCartButton.removeAttribute('disabled');
          addToCartButton.classList.remove('is--out-of-stock');
        }, 0);
      }
    }
  }

  updateDynamicDescription(variantId) {
    const dynamicDescription = document.querySelector(`.dynamic-description-item[data-variant-id="${variantId.trim()}"]`);

    const mainDescription = document.querySelector('.product-main__description .product-description');
    if (mainDescription && dynamicDescription) {
      mainDescription.innerHTML = dynamicDescription.querySelector('.metafield-rich_text_field').innerHTML;
    }
  }

  /**
   * Format money with fallback for when Shopify.formatMoney is not available
   */
  formatMoney(cents) {
    // Check if Shopify.formatMoney is available
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents);
    }

    // Fallback: simple dollar formatting
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }

    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  /**
   * Update pricing display
   */
  updatePricingDisplay(totalPrice, comparePrice, variantId, subscriptionDiscountPercentage = 0, quantity = 1, isOnetime = false, otpDiscount = 0) {
    if (this.elements.totalPriceElement && totalPrice) {
      this.elements.totalPriceElement.textContent = this.formatMoney(totalPrice);
    }

    var oneTimeSavingsOffset = 0;
    if (parseInt(comparePrice) > 0) {
      oneTimeSavingsOffset = (Number(otpDiscount) / 100) / Number(quantity);
    }

    if (!isOnetime) {
      if (subscriptionDiscountPercentage && parseInt(subscriptionDiscountPercentage) > 0) {
        subscriptionDiscountPercentage = (100 - parseInt(subscriptionDiscountPercentage)) / 100;
      }
    }

    if (this.elements.buttonPrice && totalPrice) {
      this.elements.buttonPrice.textContent = this.formatMoney(totalPrice);
      this.elements.buttonPrice.dataset.variantId = variantId;
      if (!isOnetime) {
        this.elements.buttonPrice.dataset.priceMultiplier = subscriptionDiscountPercentage;
        this.elements.buttonPrice.dataset.priceOffset = 0;
      } else {
        this.elements.buttonPrice.dataset.priceMultiplier = quantity;
        this.elements.buttonPrice.dataset.priceOffset = oneTimeSavingsOffset * -1;
      }
    }

    if (comparePrice && parseInt(comparePrice) > parseInt(totalPrice)) {
      const savings = parseInt(comparePrice) - parseInt(totalPrice);

      if (this.elements.originalPriceElement && this.elements.originalAmountElement) {
        this.elements.originalAmountElement.textContent = this.formatMoney(comparePrice);
        this.elements.originalPriceElement.style.display = 'block';
      }

      if (this.elements.savingsElement && this.elements.savingsAmountElement) {
        this.elements.savingsAmountElement.textContent = this.formatMoney(savings);
        this.elements.savingsElement.style.display = 'block';
      }

      if (this.elements.buttonComparePrice) {
        this.elements.buttonComparePrice.textContent = this.formatMoney(comparePrice);
        this.elements.buttonComparePrice.style.display = 'inline';
        this.elements.buttonComparePrice.dataset.variantId = variantId;
        if (isOnetime) {
          this.elements.buttonComparePrice.dataset.priceMultiplier = quantity;
        } else {
          this.elements.buttonComparePrice.dataset.priceMultiplier = 1;
        }
      }
    } else {
      if (this.elements.originalPriceElement) this.elements.originalPriceElement.style.display = 'none';
      if (this.elements.savingsElement) this.elements.savingsElement.style.display = 'none';
      if (this.elements.buttonComparePrice) {
        this.elements.buttonComparePrice.style.display = 'none';
        this.elements.buttonComparePrice.dataset.variantId = variantId;
        if (isOnetime) {
          this.elements.buttonComparePrice.dataset.priceMultiplier = 1;
        }
      }
    }
  }

  /**
   * Set loading state
   */
  setLoadingState(loading) {
    if (loading) {
      if (this.elements.submitButton) this.elements.submitButton.disabled = true;
      if (this.elements.buttonSpinner) this.elements.buttonSpinner.classList.remove('hidden');
      if (this.elements.buttonText) this.elements.buttonText.style.opacity = '0.7';
    } else {
      if (this.elements.submitButton) this.elements.submitButton.disabled = false;
      if (this.elements.buttonSpinner) this.elements.buttonSpinner.classList.add('hidden');
      if (this.elements.buttonText) this.elements.buttonText.style.opacity = '1';
    }
  }

  /**
   * Show success notification
   */
  showSuccessNotification(cartItem) {
    if (this.elements.cartNotificationWrapper && this.elements.cartNotificationProductName) {
      this.elements.cartNotificationProductName.textContent = cartItem.product_title || 'Product';
      this.elements.cartNotificationWrapper.style.display = 'block';

      // Auto hide after 3 seconds
      setTimeout(() => {
        this.elements.cartNotificationWrapper.style.display = 'none';
      }, 3000);
    }
  }

  /**
   * Show error notification
   */
  showErrorNotification(message) {
    if (this.elements.errorNotificationWrapper && this.elements.errorNotificationText) {
      this.elements.errorNotificationText.textContent = message;
      this.elements.errorNotificationWrapper.style.display = 'block';

      // Auto hide after 5 seconds
      setTimeout(() => {
        this.elements.errorNotificationWrapper.style.display = 'none';
      }, 5000);
    }
  }

  /**
   * Hide all notifications
   */
  hideNotifications() {
    if (this.elements.cartNotificationWrapper) this.elements.cartNotificationWrapper.style.display = 'none';
    if (this.elements.errorNotificationWrapper) this.elements.errorNotificationWrapper.style.display = 'none';
  }

  /**
   * Handle variant changes
   */
  handleVariantChange() {
    const el = document.querySelector('[data-selected-variant]');
    if (!el) return;
    const variantData = JSON.parse(el.textContent);

    if (this.elements.variantInput && variantData) {
      this.elements.variantInput.value = variantData.id;
    }

    // Refresh pricing based on current selection
    this.updatePurchaseType();
  }

  /**
   * Initialize options on load
   */
  initializeOptions() {
    console.log('Initializing options for element:', this.id, 'isActive:', this.isActive);

    // Check if alternate layout mode (force default variant)
    const isAlternateLayout = this.dataset.selectLowest === 'true';
    const hasUrlSelection = (() => {
      const params = new URLSearchParams(window.location.search);
      return Boolean(params.get('variant') || params.get('quantity') || params.get('otp'));
    })();
    const precheckedOption = this.querySelector('.subscription-option__input:checked, .onetime-option__input:checked');

    // Check for URL parameters to set initial state
    const urlParams = new URLSearchParams(window.location.search);
    let variantId = urlParams.get('variant');
    let quantity = urlParams.get('quantity'); // Standard Shopify param
    let otp = urlParams.get('otp'); // User suggested fallback

    // Check for "Force Default" settings on groups
    const subscriptionContent = this.querySelector('.purchase-options__content--subscription');
    const onetimeContent = this.querySelector('.purchase-options__content--onetime');

    // Check if either group has forced default enabled
    const forceSubscription = subscriptionContent && subscriptionContent.dataset.forceDefault === 'true';
    const forceOnetime = onetimeContent && onetimeContent.dataset.forceDefault === 'true';

    // If a group forces default, ignore URL params and alternate layout flag
    if (forceSubscription || forceOnetime) {
      console.log('Force default enabled:', { forceSubscription, forceOnetime });

      // Attempt to find the default selected option within the forced group
      // This ensures we pick the specific option marked as "Selected by default" in settings
      let defaultOption = null;
      if (forceSubscription && subscriptionContent) {
        defaultOption = subscriptionContent.querySelector('input[checked]');
      } else if (forceOnetime && onetimeContent) {
        defaultOption = onetimeContent.querySelector('input[checked]');
      }

      if (defaultOption) {
        // Use the data from the default option to ensure strict matching selects it
        variantId = defaultOption.dataset.variantId || defaultOption.value;
        quantity = defaultOption.dataset.quantity || null;
        // Set otp if it's a onetime option
        if (forceOnetime) {
          otp = quantity;
        } else {
          otp = null;
        }
        console.log('Found forced default option:', { variantId, quantity, otp });
      } else {
        variantId = null;
        quantity = null;
        otp = null;
      }
    } else {
      // Only run standard URL/Alternate logic if NOT forced

      // When alternate layout is enabled, ALWAYS force the default variant (ignore URL)
      if (isAlternateLayout && this.dataset.defaultVariantId) {
        variantId = this.dataset.defaultVariantId;
        quantity = null; // Clear quantity to match default
        otp = null;
        console.log('Alternate layout: Forcing default variant:', variantId);
      } else if (!variantId && this.dataset.defaultVariantId) {
        // Explicit default from settings should override Liquid-prechecked fallbacks
        // when no URL variant is provided.
        variantId = this.dataset.defaultVariantId;
        console.log('Using default variant from settings:', variantId);
      }
    }

    let matchedOption = null;
    let targetType = null;

    if (variantId) {
      // Try to find a matching option
      const allOptions = this.querySelectorAll('.subscription-option__input, .onetime-option__input');

      for (const option of allOptions) {
        const optVariantId = option.dataset.variantId || option.value;
        const optQuantity = option.dataset.quantity || '1';

        // Check if variant matches
        if (optVariantId === variantId) {
          // If quantity/otp is provided, it must match
          // (Use loosely equal for string/number comparison)
          if ((quantity || otp) && optQuantity != (quantity || otp)) continue;

          // Found a match!
          matchedOption = option;
          targetType = option.classList.contains('subscription-option__input') ? 'subscription' : 'onetime';

          // If we have an exact match on quantity, stop looking.
          if (quantity || otp) break;
        }
      }
    }

    if (!matchedOption && precheckedOption && !hasUrlSelection && !forceSubscription && !forceOnetime) {
      matchedOption = precheckedOption;
      targetType = precheckedOption.classList.contains('subscription-option__input') ? 'subscription' : 'onetime';
      console.log('Using prechecked option from Liquid:', { targetType });
    }

    if (matchedOption && targetType) {
      console.log('Found matching option from URL:', { variantId, quantity, targetType });

      // Select the option
      matchedOption.checked = true;

      // Update UI for the selected option
      if (targetType === 'subscription') {
        this.clearOptionSelection('.subscription-option__input', '.subscription-option');
        this.clearOptionSelection('.onetime-option__input', '.onetime-option');
        matchedOption.checked = true;
        matchedOption.closest('.subscription-option').classList.add('subscription-option--selected');

        // Manually handle tab switch to avoid selectSubscription() overriding our specific option selection
        const subscriptionButton = this.querySelector('.purchase-options__tab-nav[data-purchase-type="subscription"]');
        const subscriptionInput = this.querySelector('input[data-purchase-type="subscription"]');

        // Reset tabs
        this.elements.tabButtons.forEach(btn => {
          btn.classList.remove('active');
          btn.setAttribute('aria-selected', 'false');
        });
        this.elements.tabInputs.forEach(input => input.checked = false);

        // Activate subscription tab
        if (subscriptionButton) {
          subscriptionButton.classList.add('active');
          subscriptionButton.setAttribute('aria-selected', 'true');
        }
        if (subscriptionInput) subscriptionInput.checked = true;

        // Show content
        if (subscriptionContent) subscriptionContent.classList.add('active');
        if (onetimeContent) onetimeContent.classList.remove('active');

        // Trigger update
        this.updatePurchaseType();
      } else {
        this.clearOptionSelection('.subscription-option__input', '.subscription-option');
        this.clearOptionSelection('.onetime-option__input', '.onetime-option');
        matchedOption.checked = true;
        matchedOption.closest('.onetime-option').classList.add('onetime-option--selected');

        // Switch to one-time tab
        const onetimeButton = this.querySelector('.purchase-options__tab-nav[data-purchase-type="onetime"]');
        if (onetimeButton) {
          // Trigger click to handle UI switching
          const buttonClickEvent = new Event('click', { bubbles: true });
          onetimeButton.dispatchEvent(buttonClickEvent);
        } else {
          // Fallback if no button (e.g. only input)
          // If we have a matched option but no tabs/buttons (e.g. A/B test nested block),
          // and limited content structure, explicitly update the inputs.
          const contentElements = this.querySelectorAll('.purchase-options__content');

          if (contentElements.length <= 1) {
            console.log('Matched one-time variant with no tabs. Forcing update.');
            this.updateOnetimePurchase();
          } else {
            this.handleTabSwitch();
          }
        }
      }
    } else {
      // Default behavior if no URL params or no match found

      // Select options for both subscription and one-time
      this.selectDefaultOrFirstOption('.subscription-option__input', '.subscription-option');
      this.selectDefaultOrFirstOption('.onetime-option__input', '.onetime-option');

      // Decide which tab to default to based on forced flags or default subscription
      if (forceOnetime) {
        // Manually switch to onetime if forced
        const onetimeButton = this.querySelector('.purchase-options__tab-nav[data-purchase-type="onetime"]');
        if (onetimeButton) {
          const buttonClickEvent = new Event('click', { bubbles: true });
          onetimeButton.dispatchEvent(buttonClickEvent);
        } else {
          // Fallback for no button
          if (subscriptionContent) subscriptionContent.classList.remove('active');
          if (onetimeContent) onetimeContent.classList.add('active');
          const onetimeInput = this.querySelector('input[data-purchase-type="onetime"]');
          if (onetimeInput) onetimeInput.checked = true;
          this.updatePurchaseType();
        }
      } else {
        // Default to subscription (standard behavior)
        this.selectSubscription();
      }
    }

    // Re-check active state after a delay in case product toggle script runs later
    setTimeout(() => {
      const wasActive = this.isActive;
      this.detectActiveState();

      // If we just became active, make sure subscription is selected and pricing is updated
      if (!wasActive && this.isActive) {
        console.log('Element became active after delayed detection:', this.id);
        // Only force subscription if we didn't match from URL AND we aren't forcing one-time
        if (!matchedOption && !forceOnetime) {
          this.selectSubscription();
        } else {
          // If we matched an option from URL, OR we are forcing one-time, simply update
          this.updatePurchaseType();
        }
      } else if (this.isActive) {
        console.log('Element was already active, ensuring pricing is updated:', this.id);
        this.updatePurchaseType();
      }
    }, 200);
  }

  /**
   * Update URL with current selection
   */
  updateUrl(variantId, sellingPlan, quantity) {
    // Prevent URL updates if inside a quick add modal or on a collection page
    const isQuickAdd = this.closest('[data-quick-add-modal]');
    const isCollectionPage = document.body.classList.contains('template-collection');
    // Prevent URL updates when alternate layout is enabled (forces default on each page load)
    const isAlternateLayout = this.dataset.selectLowest === 'true';

    if (isQuickAdd || isCollectionPage || isAlternateLayout) {
      return;
    }

    if (!window.history.replaceState) return;

    const url = new URL(window.location.href);
    const params = url.searchParams;

    if (variantId) {
      params.set('variant', variantId);
    } else {
      params.delete('variant');
    }

    // Always remove selling_plan to keep URLs clean
    params.delete('selling_plan');

    // Handle quantity - only add if > 1
    if (quantity && quantity !== '1') {
      params.set('quantity', quantity);
    } else {
      params.delete('quantity');
    }

    // Remove OTP param if we are using quantity now, to clean up
    params.delete('otp');

    const newUrl = `${url.pathname}${params.toString() ? '?' + params.toString() : ''}${url.hash}`;

    // Only update if changed to avoid spamming history
    if (newUrl !== window.location.href) {
      window.history.replaceState({ path: newUrl }, '', newUrl);
    }
  }

  /**
   * Reset to default state when switching products
   */
  resetToDefaultState(defaultToSubscription = true) {
    console.log('Resetting purchase option to default state:', this.id, 'defaultToSubscription:', defaultToSubscription);

    // Always select subscription when resetting (force subscription behavior)
    this.selectSubscription();
  }

  /**
   * Setup mutation observer to watch for active class changes
   */
  setupMutationObserver() {
    this.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          this.handleActiveStateChange();
        }
      });
    });

    this.mutationObserver.observe(this, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  /**
   * Handle when the active class changes on this element
   */
  handleActiveStateChange() {
    const wasActive = this.isActive;
    this.detectActiveState();

    // If this element just became active, update inputs and pricing
    if (!wasActive && this.isActive) {
      console.log('Purchase option became active:', this.id);
      // Use setTimeout to ensure the DOM updates are complete
      setTimeout(() => {
        this.updatePurchaseType();
      }, 50);
    }
  }

  /**
   * Handle external product changes (from product toggle outside this element)
   */
  handleExternalProductChange(e) {
    // Handle product-tab:switched event specifically
    if (e.type === 'product-tab:switched') {
      const { purchaseOptionsElement } = e.detail;

      // Check if this event is for this specific purchase options element
      if (purchaseOptionsElement === this) {
        console.log('This purchase option was switched to, updating state...');

        // Re-detect active state and update if needed
        this.detectActiveState();

        // Force update purchase type since we just became active
        if (this.isActive) {
          // Use setTimeout to ensure all DOM updates are complete
          setTimeout(() => {
            this.updatePurchaseType();
          }, 100);
        }
      }
    } else {
      // Handle other events like product-tab:change or purchase-option:activate
      console.log('External product change event received:', e.type, e.detail);

      // Re-detect active state for any external change
      this.detectActiveState();

      // If we're now active, update our state
      if (this.isActive) {
        setTimeout(() => {
          this.updatePurchaseType();
        }, 50);
      }
    }
  }
}

// Register the custom element
customElements.define('purchase-options', PurchaseOptionsElement);

const discountCache = {};

const STOREFRONT_ACCESS_TOKEN = 'b454d94072f9950d78734aa7e505e186';
const STOREFRONT_API_URL = '/api/2024-04/graphql.json';

const STOREFRONT_QUERY = `
  mutation cartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id
        discountCodes {
          code
          applicable
        }
        lines(first: 50) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                }
              }
              sellingPlanAllocation {
                sellingPlan {
                  id
                  name
                }
              }
              discountAllocations {
                discountedAmount {
                  amount
                  currencyCode
                }
                ... on CartCodeDiscountAllocation {
                  code
                  discountApplication {
                    allocationMethod
                    targetSelection
                    targetType
                    value {
                      ... on PricingPercentageValue {
                        percentage
                      }
                      ... on MoneyV2 {
                        amount
                        currencyCode
                      }
                    }
                  }
                }
                ... on CartAutomaticDiscountAllocation {
                  title
                  discountApplication {
                    allocationMethod
                    targetSelection
                    targetType
                    value {
                      ... on PricingPercentageValue {
                        percentage
                      }
                      ... on MoneyV2 {
                        amount
                        currencyCode
                      }
                    }
                  }
                }
                ... on CartCustomDiscountAllocation {
                  title
                  discountApplication {
                    allocationMethod
                    targetSelection
                    targetType
                    value {
                      ... on PricingPercentageValue {
                        percentage
                      }
                      ... on MoneyV2 {
                        amount
                        currencyCode
                      }
                    }
                  }
                }
              }
            }
          }
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
          totalTaxAmount {
            amount
            currencyCode
          }
          totalDutyAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;


/**
 * Bulk discount check via Storefront API (Headless Shadow Cart)
 */
async function applyDiscountCodeToCart(discountCode) {
  if (!discountCode) return;

  const normalize = (value) => (value || '').trim().toLowerCase();
  const routeRoot = window.Shopify?.routes?.root || '/';

  try {
    const cartResponse = await fetch(`${routeRoot}cart.js`, {
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!cartResponse.ok) {
      throw new Error(`Cart fetch failed: ${cartResponse.status}`);
    }

    const cart = await cartResponse.json();

    const appliedCodes = [
      ...(cart.cart_level_discount_applications || []).map((item) =>
        normalize(item.title || item.discount_application?.title)
      ),
      ...(cart.discount_codes || [])
        .filter((item) => item.applicable !== false)
        .map((item) => normalize(item.code)),
    ];

    const normalizedDiscountCode = normalize(discountCode);

    if (appliedCodes.includes(normalizedDiscountCode)) {
      return;
    }

    const normalizedCodes = new Set();

    const discountString = [
      ...(cart.cart_level_discount_applications || []).map((item) =>
        item.title || item.discount_application?.title
      ),
      ...(cart.discount_codes || []).map((item) => item.code),
      discountCode,
    ]
      .filter(Boolean)
      .filter((item) => {
        const normalizedCode = normalize(item);

        if (normalizedCodes.has(normalizedCode)) return false;

        normalizedCodes.add(normalizedCode);
        return true;
      })
      .join(',');

    const formData = new FormData();
    formData.append('discount', discountString);

    const updateResponse = await fetch(`${routeRoot}cart/update.js`, {
      method: 'POST',
      body: formData,
      headers: {
        Accept: 'application/json',
      },
    });

    if (!updateResponse.ok) {
      throw new Error(`Discount update failed: ${updateResponse.status}`);
    }

    const updatedCart = await updateResponse.json();

    const updatedCodes = [
      ...(updatedCart.cart_level_discount_applications || []).map((item) =>
        normalize(item.title || item.discount_application?.title)
      ),
      ...(updatedCart.discount_codes || [])
        .filter((item) => item.applicable !== false)
        .map((item) => normalize(item.code)),
    ];

    if (!updatedCodes.includes(normalizedDiscountCode)) {
      console.warn('Discount was submitted but not applied:', discountCode);
    }

  } catch (error) {
    console.error('Discount apply error:', error);
  }
}

async function checkUniversalDiscount(discountCode) {
  if (window.isCheckingDiscount) return;

  const inputElements = document.querySelectorAll('.subscription-group .subscription-option input');
  if (inputElements.length === 0) return;

  const allItems = Array.from(inputElements).map(input => ({
    variantId: input.dataset.variantId,
    sellingPlanId: input.dataset.sellingPlan || null,
    cacheKey: `${input.dataset.variantId}-${input.dataset.sellingPlan || null}-${discountCode.replaceAll(' ', '_')}`
  }));

  const filteredItems = allItems.filter(item => {
    if (discountCache[item.cacheKey]) {
      resultDynamicSaving(discountCache[item.cacheKey]);
      return false;
    }
    return true;
  });

  if (filteredItems.length === 0) return;

  const saveText = document.querySelector('.discount-saving-text');
  if (saveText) saveText.classList.add('blur-pulse');

  window.isCheckingDiscount = true;

  try {
    const initialCart = await (await fetch(window.Shopify.routes.root + 'cart.js')).json();
    const initialCodes = [
      ...(initialCart.cart_level_discount_applications?.map(a => a.title) || []),
      ...(initialCart.discount_codes?.map(dc => dc.code) || [])
    ].filter(Boolean);

    window.initialCartCodes = initialCodes;
    const combinedDiscounts = [...new Set([...initialCodes, discountCode])];

    const linesInput = filteredItems.map(item => {
      const line = {
        merchandiseId: `gid://shopify/ProductVariant/${item.variantId}`,
        quantity: 1
      };
      if (item.sellingPlanId && item.sellingPlanId !== "null") {
        line.sellingPlanId = `gid://shopify/SellingPlan/${item.sellingPlanId}`;
      }
      return line;
    });

    const variables = {
      input: {
        lines: linesInput,
        discountCodes: combinedDiscounts
      }
    };

    const response = await fetch(STOREFRONT_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN
      },
      body: JSON.stringify({ query: STOREFRONT_QUERY, variables })
    });

    const { data, errors } = await response.json();

    if (errors || data?.cartCreate?.userErrors?.length) {
      console.error("Storefront API Errors:", errors || data.cartCreate.userErrors);
      throw new Error("Failed to calculate discounts via Storefront");
    }

    const sfCart = data.cartCreate.cart;

    filteredItems.forEach(item => {
      const res = {
        applied: false,
        discount_level: 'line_item',
        type: 'fixed_amount',
        allocation_method: 'each',
        value: 0,
        amount_saved: 0,
        code: discountCode,
        variantId: item.variantId,
        sellingPlanId: item.sellingPlanId
      };

      const targetEdge = sfCart.lines.edges.find(({ node }) => {
        const isVariantMatch = node.merchandise.id.includes(item.variantId);
        const isSellingPlanMatch = item.sellingPlanId && item.sellingPlanId !== "null"
          ? node.sellingPlanAllocation?.sellingPlan.id.includes(item.sellingPlanId)
          : true;
        return isVariantMatch && isSellingPlanMatch;
      });

      if (targetEdge) {
        const node = targetEdge.node;

        const allocation = node.discountAllocations?.find(a =>
          a.code?.toLowerCase() === discountCode.toLowerCase()
        );

        if (allocation) {
          const app = allocation.discountApplication;
          res.applied = true;
          res.allocation_method = app.allocationMethod ? app.allocationMethod.toLowerCase() : 'each';

          if (app.value?.percentage) {
            res.type = 'percentage';
            res.value = parseFloat(app.value.percentage);
          } else if (app.value?.amount) {
            res.type = 'fixed_amount';
            res.value = parseFloat(app.value.amount);
          }

          res.amount_saved = Math.ceil(parseFloat(allocation.discountedAmount.amount) * 100);
        }
      }

      discountCache[item.cacheKey] = res;
    });

    const hasAppliedDiscount = filteredItems.some(item => {
      return discountCache[item.cacheKey]?.applied === true;
    });

    if (hasAppliedDiscount) {
      await applyDiscountCodeToCart(discountCode);
    }

  } catch (error) {
    console.error("Critical error in Storefront API calculation:", error);
  } finally {
    window.isCheckingDiscount = false;
    filteredItems.forEach(item => resultDynamicSaving());
  }
}

// Simple debounce helper
function debounceSaving(func, timeout = 350) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => { func.apply(this, args); }, timeout);
  };
}

// Wrap your call
const runDiscountCheck = debounceSaving(async (code) => {
  await checkUniversalDiscount(code);
}, 300);

/**
 * Updates UI with universal logic for "Once per order" and "Each item" discounts.
 */
function parsePriceFromText(text) {
  if (!text) return null;

  const match = text.replace(',', '.').match(/\d+(\.\d+)?/);
  return match ? parseFloat(match[0]) : null;
}

function formatPriceAmount(price, currencySymbol, shouldRound = false) {
  const normalizedPrice = shouldRound ? Math.round(price) : Number(price);
  const formattedPrice = Number.isInteger(normalizedPrice) ? normalizedPrice : normalizedPrice.toFixed(2);

  return `${currencySymbol}${formattedPrice}`;
}

function replacePriceInText(text, newPrice, currencySymbol, shouldRound = false) {
  if (!text) return text;

  return text.replace(
    /\$?\d+(\.\d+)?/,
    formatPriceAmount(newPrice, currencySymbol, shouldRound)
  );
}

function resultDynamicSaving() {
  document.body.classList.remove('discount-saving-loading');

  const allOptions = document.querySelectorAll('.subscription-group .subscription-option');
  const saveText = document.querySelector('.discount-saving-text');
  const atcButton = document.querySelector('.purchase-summary .purchase-summary__add-to-cart.btn');
  const priceDisplay = atcButton?.querySelector('.button-price');
  const comparePrice = atcButton?.querySelector('.button-compare-price');

  if (allOptions.length === 0) return;

  allOptions.forEach(option => {
    const input = option.querySelector('input');
    if (!input) return;

    const variantId = input.dataset.variantId;
    const sellingPlanId = input.dataset.sellingPlan || "null";
    const currentCode = window.savingDiscountCode;

    if (!currentCode) return;

    const cacheKey = `${variantId}-${sellingPlanId}-${currentCode.replaceAll(' ', '_')}`;
    const data = discountCache[cacheKey];

    const isSelected = option.classList.contains('subscription-option--selected');
    const badges = option.querySelectorAll('.subscription-option__savings-badge');

    const isAlreadyAppliedInCart = window.initialCartCodes?.some(
      code => code.toLowerCase() === currentCode.toLowerCase()
    );

    if (data && data.applied) {
      const isOncePerOrder = data.allocation_method === 'across';
      const shouldApplyDiscountToUI = isOncePerOrder ? !isAlreadyAppliedInCart : true;

      // ATC Button Logic (Honest Price)
      const effectiveDiscountCents = shouldApplyDiscountToUI ? data.amount_saved : 0;
      const discountAmountValue = parseFloat(effectiveDiscountCents / 100);

      // Badge Logic (Marketing Price - Always show full discount)
      const marketingDiscountCents = data.amount_saved;

      // 1. Update Savings Badges
      if (badges.length > 0) {
        badges.forEach(badge => {
          const { amount: currentSavingCents } = badge.dataset;
          if (currentSavingCents) {
            const totalSavingCents = Number(marketingDiscountCents) + Number(currentSavingCents);
            const originalPriceEl = option.querySelector('.subscription-option__original-price');
            const currentPriceEl = option.querySelector('.subscription-option__price strong');

            const originalPrice = parsePriceFromText(originalPriceEl?.innerText);
            const variantInput = option.querySelector('input[name="variant_id"]');
            const months = Number(variantInput?.dataset.months) || 1;

            const savePrice = (totalSavingCents / 100) / months;

            if (originalPrice !== null && originalPriceEl) {
              originalPriceEl.innerText = replacePriceInText(
                originalPriceEl.innerText,
                originalPrice,
                window.currencySymbolSaving
              );
            }

            if (originalPrice !== null && currentPriceEl) {
              const newPrice = originalPrice - savePrice;

              currentPriceEl.innerText = replacePriceInText(
                currentPriceEl.innerText,
                newPrice,
                window.currencySymbolSaving,
                true
              );
            }
            const displayPrice = `${window.currencySymbolSaving}${Math.round(totalSavingCents / 100)}`;
            const oldPriceFormatted = `${window.currencySymbolSaving}${Math.round(Number(currentSavingCents) / 100)}`;

            badge.innerHTML = '';
            badge.classList.add('is--modified');

            let oldPriceSpan = document.createElement('span');
            oldPriceSpan.innerText = oldPriceFormatted;
            oldPriceSpan.style.textDecoration = 'line-through';
            oldPriceSpan.style.order = 2;
            oldPriceSpan.style.paddingLeft = '5px';
            oldPriceSpan.style.opacity = '0.7';
            oldPriceSpan.classList.add('c-old-price');

            let newPriceSpan = document.createElement('span');
            newPriceSpan.classList.add('new-saving-amount');
            newPriceSpan.innerText = `Save ${displayPrice}`;
            newPriceSpan.style.order = 1;

            badge.appendChild(newPriceSpan);
            badge.appendChild(oldPriceSpan);
          }
        });
      }

      // 2. Update elements for the SELECTED option
      if (isSelected) {
        if (saveText) {
          const content = saveText.querySelector('.context');
          if (content) {
            if (saveText.classList.contains('blur-pulse')) {
              saveText.classList.remove('blur-pulse');
            }

            const { text, currencySymbol: symbol } = content.dataset;

            const selectedSavingAmountEl = option.querySelector('.new-saving-amount');

            let rawAmount = null;

            if (selectedSavingAmountEl) {
              rawAmount = parsePriceFromText(selectedSavingAmountEl.innerText);
            }

            if (rawAmount !== null) {
              saveText.classList.remove('non-active');

              let finalValue;

              if (window.alwaysHideCents) {
                finalValue = Math.floor(rawAmount);
              } else if (window.hideTrailingZeros) {
                finalValue = Number(parseFloat(rawAmount).toFixed(2));
              } else {
                finalValue = rawAmount.toFixed(2);
              }

              content.innerText = text
                .replace('[X amount]', `${symbol}${finalValue}`)
                .replace('[XX discount code]', data.code);
            } else {
              saveText.classList.add('non-active');
            }
          }
        }

        if (priceDisplay && atcButton.dataset.price) {
          const rawPrice = atcButton.dataset.price;
          const basePrice = parseFloat(rawPrice.replace(/[^0-9.]/g, ''));

          if (!isNaN(basePrice)) {
            const finalPrice = basePrice - discountAmountValue;
            const rounded = Math.round(finalPrice);

            priceDisplay.innerText = `${window.currencySymbolSaving}${rounded}`;

            if (comparePrice) {
              const compareRaw = comparePrice.innerText;
              const compareBase = parseFloat(compareRaw.replace(/[^0-9.]/g, ''));

              if (!isNaN(compareBase)) {
                comparePrice.innerText = formatPriceAmount(compareBase, window.currencySymbolSaving);
              }
            }
          }
        }
      }
    } else if (isSelected) {
      if (saveText) {
        saveText.classList.add('non-active');
        saveText.classList.remove('blur-pulse');
      }
    }
  });

  const oneTimeSelected = document.querySelector('.onetime-option.onetime-option--selected');

  if (oneTimeSelected) {
    if (saveText) saveText.classList.add('non-active');

    if (priceDisplay && atcButton?.dataset.price) {
      const rawPrice = atcButton.dataset.price;
      const basePrice = parseFloat(rawPrice.replace(/[^0-9.]/g, ''));

      if (!isNaN(basePrice)) {
        priceDisplay.innerText = formatPriceAmount(basePrice, window.currencySymbolSaving);
      }
    }

    if (comparePrice) {
      const compareRaw = comparePrice.innerText;
      const compareBase = parseFloat(compareRaw.replace(/[^0-9.]/g, ''));

      if (!isNaN(compareBase)) {
        comparePrice.innerText = formatPriceAmount(compareBase, window.currencySymbolSaving);
      }
    }
  }
}


/**
 * Intelligems: build product + variant payload (string ids) from ATC button context.
 */
function buildIntelligemsProductPayload(atcButton) {
  const form = atcButton.closest('form');
  const variantInput = form?.querySelector('input[name="id"], input.variant_input');
  const variantId = variantInput?.value ? String(variantInput.value) : '';

  let productId = '';
  let productTitle = '';
  let variantTitle = '';

  try {
    const productJsonEl = document.querySelector('[data-product-json]');
    if (productJsonEl?.textContent) {
      const productData = JSON.parse(productJsonEl.textContent);
      productId = productData.id != null ? String(productData.id) : '';
      productTitle = typeof productData.title === 'string' ? productData.title : '';
      const match = (productData.variants || []).find(
        (v) => v && (String(v.id) === variantId || Number(v.id) === Number(variantId)),
      );
      if (match?.title) variantTitle = match.title;
    }
  } catch (_) {
    /* ignore malformed JSON */
  }

  if (!productId) {
    const pf = atcButton.closest('product-form') || document.querySelector('product-form[data-product-id]');
    const pid = pf?.dataset?.productId;
    if (pid) productId = String(pid);
  }

  if (!variantTitle && variantId) {
    try {
      const selectedEl = document.querySelector('[data-selected-variant]');
      if (selectedEl?.textContent) {
        const vd = JSON.parse(selectedEl.textContent);
        if (vd && String(vd.id) === variantId && vd.title) variantTitle = vd.title;
      }
    } catch (_) {
      /* ignore */
    }
  }

  return {
    productId,
    productTitle,
    variantId,
    variantTitle,
  };
}

// Intelligems ATC event

document.body.addEventListener('click', (e) => {
  const addToCartButton = e.target.closest('.purchase-summary__add-to-cart[type="submit"]');

  if (addToCartButton) {
    const form = addToCartButton.closest('form');
    const monthInput = form?.querySelector('input.months_input, input[name="properties[_months]"]');
    const month = monthInput ? monthInput.value : '';
    const product = buildIntelligemsProductPayload(addToCartButton);

    window.igEvents = window.igEvents || [];
    window.igEvents.push({
      event: 'variant-cadence',
      month,
      product,
    });

    console.log('Intelligems: Clean ATC Tracked (javascripteventJLOXO)');
  }
});

// Intelligems custom event
document.body.addEventListener('click', (e) => {
  if (window.enableSavingLogic) {
    const addToCartButton = e.target.closest('.purchase-summary__add-to-cart[type="submit"]');

    if (addToCartButton) {
      window.igEvents = window.igEvents || [];
      window.igEvents.push({
        "event": "javascripteventJLOXO"
      });

      console.log('Intelligems: Clean ATC Tracked (javascripteventJLOXO)');
    }
  }
});